import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { toastApiError } from "@/lib/toastApiError";
import {
  ArrowLeft,
  Eye,
  EyeOff,
  LayoutDashboard,
  Lock,
  LogIn,
  User,
  UserPlus,
} from "lucide-react";
import siteLogo, { SITE_LOGO_ALT } from "@/assets/brandLogo";
import type { StaffRole } from "@/lib/authApi";
import {
  buildFullName,
  decodeUserFromToken,
  getStaffAccessToken,
  resendSignupOtp,
  sendSignupOtpEmail,
  setStaffTokens,
  signupComplete,
  updateProfileRemote,
  validateSignupPassword,
  verifySignupOtp,
} from "@/lib/authApi";
import { setStoredStaffRbac } from "@jl/lib/jainLexiconRbac";
import { cn } from "@/lib/utils";

type RegisterStep = "details" | "otp";

const RESEND_SECONDS = 60;

const StaffLogin = () => {
  const navigate = useNavigate();
  const { signInStaff, staffUser, canAccessStaffDashboard, isLoading, hydrateStaffFromStoredTokens } =
    useAuth();
  const [staffKind, setStaffKind] = useState<StaffRole>("admin");
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [otpForEmail, setOtpForEmail] = useState("");
  const [signupCodeReference, setSignupCodeReference] = useState("");
  const [registerStep, setRegisterStep] = useState<RegisterStep>("details");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showRegPassword, setShowRegPassword] = useState(false);
  const [showRegConfirmPassword, setShowRegConfirmPassword] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const [signupFirstName, setSignupFirstName] = useState("");
  const [signupLastName, setSignupLastName] = useState("");

  useEffect(() => {
    if (!isLoading && staffUser && canAccessStaffDashboard) {
      navigate("/admin", { replace: true });
    }
  }, [staffUser, canAccessStaffDashboard, isLoading, navigate]);

  useEffect(() => {
    setShowPassword(false);
  }, []);

  useEffect(() => {
    setRegisterStep("details");
    setOtpCode("");
    setOtpForEmail("");
    setSignupCodeReference("");
    setShowRegPassword(false);
    setShowRegConfirmPassword(false);
    setSignupFirstName("");
    setSignupLastName("");
  }, [mode]);

  useEffect(() => {
    if (registerStep !== "otp" || resendIn <= 0) {
      return;
    }
    const t = setInterval(() => {
      setResendIn(prev => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(t);
  }, [registerStep, resendIn]);

  const roleSegment = (
    <div className="md:mb-6 mb-2 grid w-full max-w-sm grid-cols-2 gap-1 rounded-xl border bg-muted/40 p-1">
      {(
        [
          ["admin", "Admin"] as const,
          ["scholar", "Scholar"] as const,
        ]
      ).map(([value, label]) => (
        <button
          key={value}
          type="button"
          onClick={() => setStaffKind(value)}
          className={cn(
            "rounded-lg py-2.5 text-sm font-semibold transition-colors",
            staffKind === value
              ? "bg-primary text-primary-foreground shadow-sm"
              : "text-muted-foreground hover:bg-background/70 hover:text-foreground"
          )}
        >
          {label}
        </button>
      ))}
    </div>
  );

  const handleStaffSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    const { error, staffRole: signedRole } = await signInStaff(
      email,
      password,
      staffKind
    );
    if (error) {
      toastApiError(error.message);
      setIsSubmitting(false);
      return;
    }
    if (signedRole && signedRole !== staffKind) {
      toast.info(`Signed in as ${signedRole}. Role and permissions are set by the server.`);
    } else {
      toast.success("Signed in.");
    }
    navigate("/admin", { replace: true });
    setIsSubmitting(false);
  };

  const handleSignupContinue = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmed = email.trim();
    if (password !== confirmPassword) {
      toastApiError("Passwords do not match.");
      return;
    }
    const pwErr = validateSignupPassword(password);
    if (pwErr) {
      toastApiError(pwErr);
      return;
    }
    if (!signupFirstName.trim() || !signupLastName.trim()) {
      toastApiError("First and last name are required.");
      return;
    }

    setIsSubmitting(true);
    const { codeReference, error } = await sendSignupOtpEmail(trimmed, {
      role: staffKind,
    });
    setIsSubmitting(false);
    if (error || !codeReference) {
      toastApiError(error?.message ?? "Missing code reference from server.");
      return;
    }

    setOtpForEmail(trimmed);
    setSignupCodeReference(codeReference);
    setRegisterStep("otp");
    setOtpCode("");
    setResendIn(RESEND_SECONDS);
    toast.success("OTP sent to your email.");
  };

  const handleVerifyAndSignup = async (e: React.FormEvent) => {
    e.preventDefault();

    const currentEmail = otpForEmail || email.trim();
    if (
      otpForEmail &&
      email.trim().toLowerCase() !== otpForEmail.toLowerCase()
    ) {
      toastApiError("Email changed. Go back and request a new OTP.");
      return;
    }

    if (password !== confirmPassword) {
      toastApiError("Passwords do not match.");
      return;
    }
    const pwErr = validateSignupPassword(password);
    if (pwErr) {
      toastApiError(pwErr);
      return;
    }

    if (!signupFirstName.trim() || !signupLastName.trim()) {
      toastApiError("First and last name are required.");
      return;
    }

    if (!signupCodeReference.trim()) {
      toastApiError("Missing OTP session. Go back and request a new code.");
      return;
    }

    setIsSubmitting(true);
    try {
      const verify = await verifySignupOtp(
        currentEmail,
        otpCode,
        signupCodeReference
      );
      if (verify.error || !verify.verificationToken) {
        toastApiError(verify.error?.message ?? "Missing verification token.");
        setIsSubmitting(false);
        return;
      }

      const create = await signupComplete({
        email: currentEmail,
        password,
        password_confirm: confirmPassword,
        verification_token: verify.verificationToken,
        role: staffKind,
        account_type: staffKind,
      });
      if (create.error) {
        toastApiError(create.error.message);
        setIsSubmitting(false);
        return;
      }

      toast.success("Account created. Signing you in…");

      let signInErr: Error | null = null;
      if (create.access) {
        setStaffTokens(create.access, create.refresh ?? null);
        if (create.rbac) {
          setStoredStaffRbac(create.rbac);
        }
        const h = await hydrateStaffFromStoredTokens();
        signInErr = h.error;
      } else {
        const signInResult = await signInStaff(currentEmail, password, staffKind);
        signInErr = signInResult.error;
      }

      if (signInErr) {
        toastApiError(
          signInErr.message ??
            "Account created — sign in with your email and password."
        );
        setRegisterStep("details");
        setOtpCode("");
        setSignupCodeReference("");
        setPassword("");
        setConfirmPassword("");
        setSignupFirstName("");
        setSignupLastName("");
        setMode("signin");
        setIsSubmitting(false);
        return;
      }

      const tok = getStaffAccessToken();
      const me = tok ? decodeUserFromToken(tok) : null;
      const fn = signupFirstName.trim();
      const ln = signupLastName.trim();
      if (me?.id && fn && ln) {
        const { error: profErr } = await updateProfileRemote(
          me.id,
          {
            email: currentEmail.trim(),
            first_name: fn,
            last_name: ln,
            full_name: buildFullName(fn, ln),
          },
          "staff"
        );
        if (profErr) {
          toastApiError(
            profErr.message ??
              "Signed in, but profile could not be saved. Ask an admin or update profile if available."
          );
        }
      }

      setSignupFirstName("");
      setSignupLastName("");
      toast.success("Welcome to the admin portal.");
      navigate("/admin", { replace: true });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendIn > 0) {
      return;
    }
    const currentEmail = otpForEmail || email.trim();
    if (!signupCodeReference.trim()) {
      toastApiError("Request a new code from the previous step.");
      return;
    }
    setIsSubmitting(true);
    const { codeReference: nextRef, error } = await resendSignupOtp(
      currentEmail,
      signupCodeReference
    );
    setIsSubmitting(false);
    if (error || !nextRef) {
      toastApiError(error?.message ?? "Could not resend OTP.");
      return;
    }
    setSignupCodeReference(nextRef);
    setResendIn(RESEND_SECONDS);
    toast.success("A new OTP was sent.");
  };

  const backToSignupDetails = () => {
    setRegisterStep("details");
    setOtpCode("");
    setResendIn(0);
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="border-primary h-8 w-8 animate-spin rounded-full border-b-2" />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <section className="relative overflow-hidden bg-gradient-to-br from-[hsl(220,40%,13%)] via-[hsl(220,38%,18%)] to-[hsl(218,35%,22%)]">
        <div className="container relative py-5 md:py-10">
          <div className="flex flex-col items-center text-center">
            <img
              src={siteLogo}
              alt={SITE_LOGO_ALT}
              className="mb-4 h-16 w-auto max-w-[min(320px,90vw)] object-contain md:h-20"
            />
            <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-xl border border-white/20 bg-white/10">
              <LayoutDashboard className="text-primary h-7 w-7" />
            </div>
            <h1 className="mb-2 font-serif text-2xl font-bold text-white md:text-3xl">
              Staff login
            </h1>
            <p className="max-w-md text-sm text-white/70">
              Admin &amp; Scholar — sign in or create an admin/scholar account.
            </p>
          </div>
        </div>
      </section>

      <section className="from-secondary to-background bg-gradient-to-b py-5 md:py-10">
        <div className="container mx-auto max-w-md px-4">
          <Button
            variant="ghost"
            type="button"
            onClick={() => navigate("/")}
            className="text-muted-foreground hover:text-foreground md:mb-6 mb-4"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to site
          </Button>

          <div className="md:mb-8 mb-4 flex justify-center">{roleSegment}</div>

          <Card className="border-border/50 shadow-xl">
            <CardHeader className="pb-2 text-center">
              <div className="bg-primary/10 mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full">
                <Lock className="text-primary h-6 w-6" />
              </div>
              <CardTitle className="font-serif text-xl">
                Staff access
              </CardTitle>
              <CardDescription>
                {staffKind === "admin" ? "Admin" : "Scholar"} •{" "}
                {mode === "signin" ? "Sign in" : "Sign up"}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs
                value={mode}
                onValueChange={v =>
                  setMode(v === "signup" ? "signup" : "signin")
                }
                className="w-full"
              >
                <TabsList className="mb-4 grid h-11 w-full grid-cols-2">
                  <TabsTrigger value="signin" className="gap-1.5">
                    <LogIn className="h-4 w-4" />
                    Sign in
                  </TabsTrigger>
                  <TabsTrigger value="signup" className="gap-1.5">
                    <UserPlus className="h-4 w-4" />
                    Sign up
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="signin" className="mt-0 space-y-0">
                  <form onSubmit={handleStaffSignIn} className="md:space-y-4 space-y-3">
                    <div className="space-y-2">
                      <Label htmlFor="staff-email">Email</Label>
                      <Input
                        id="staff-email"
                        type="email"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        required
                        autoComplete="username"
                        className="h-11"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="staff-password">Password</Label>
                      <div className="relative">
                        <Input
                          id="staff-password"
                          type={showPassword ? "text" : "password"}
                          value={password}
                          onChange={e => setPassword(e.target.value)}
                          required
                          autoComplete="current-password"
                          className="h-11 pr-11"
                        />
                        <button
                          type="button"
                          tabIndex={-1}
                          onClick={() => setShowPassword(v => !v)}
                          className="text-muted-foreground hover:text-foreground absolute right-1 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-md"
                          aria-label={
                            showPassword ? "Hide password" : "Show password"
                          }
                        >
                          {showPassword ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </div>
                    <div className="flex justify-end">
                      <Link
                        to="/staff-forgot-password"
                        className="text-primary text-sm font-medium hover:underline"
                      >
                        Forgot password?
                      </Link>
                    </div>
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="bg-primary hover:bg-primary/90 h-11 w-full"
                    >
                      {isSubmitting ? (
                        <span className="flex items-center gap-2">
                          <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                          Signing in…
                        </span>
                      ) : (
                        <>
                          <LogIn className="mr-2 h-4 w-4" />
                          Continue to dashboard
                        </>
                      )}
                    </Button>
                  </form>
                </TabsContent>

                <TabsContent value="signup" className="mt-0">
                  {registerStep === "details" ? (
                    <form
                      onSubmit={handleSignupContinue}
                      className="md:space-y-4 space-y-3"
                    >
                      <div className="space-y-2">
                        <Label htmlFor="signup-email">Email</Label>
                        <Input
                          id="signup-email"
                          type="email"
                          value={email}
                          onChange={e => setEmail(e.target.value)}
                          required
                          autoComplete="username"
                          className="h-11"
                        />
                      </div>
                      <div className="grid gap-3 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label htmlFor="signup-first">First name</Label>
                          <Input
                            id="signup-first"
                            type="text"
                            value={signupFirstName}
                            onChange={e =>
                              setSignupFirstName(e.target.value)
                            }
                            required
                            autoComplete="given-name"
                            className="h-11"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="signup-last">Last name</Label>
                          <Input
                            id="signup-last"
                            type="text"
                            value={signupLastName}
                            onChange={e =>
                              setSignupLastName(e.target.value)
                            }
                            required
                            autoComplete="family-name"
                            className="h-11"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="signup-password">Password</Label>
                        <div className="relative">
                          <Input
                            id="signup-password"
                            type={showRegPassword ? "text" : "password"}
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                            required
                            autoComplete="new-password"
                            className="h-11 pr-11"
                          />
                          <button
                            type="button"
                            tabIndex={-1}
                            onClick={() => setShowRegPassword(v => !v)}
                            className="text-muted-foreground hover:text-foreground absolute right-1 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-md"
                            aria-label={
                              showRegPassword ? "Hide password" : "Show password"
                            }
                          >
                            {showRegPassword ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="signup-confirm">Confirm password</Label>
                        <div className="relative">
                          <Input
                            id="signup-confirm"
                            type={showRegConfirmPassword ? "text" : "password"}
                            value={confirmPassword}
                            onChange={e =>
                              setConfirmPassword(e.target.value)
                            }
                            required
                            autoComplete="new-password"
                            className="h-11 pr-11"
                          />
                          <button
                            type="button"
                            tabIndex={-1}
                            onClick={() =>
                              setShowRegConfirmPassword(v => !v)
                            }
                            className="text-muted-foreground hover:text-foreground absolute right-1 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-md"
                            aria-label={
                              showRegConfirmPassword
                                ? "Hide password"
                                : "Show password"
                            }
                          >
                            {showRegConfirmPassword ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      </div>
                      <Button
                        type="submit"
                        disabled={isSubmitting}
                        className="bg-primary hover:bg-primary/90 h-11 w-full"
                      >
                        {isSubmitting ? (
                          <span className="flex items-center gap-2">
                            <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                            Sending OTP…
                          </span>
                        ) : (
                          <>Send OTP to email</>
                        )}
                      </Button>
                    </form>
                  ) : (
                    <form onSubmit={handleVerifyAndSignup} className="space-y-4">
                      <p className="text-muted-foreground text-sm">
                        Enter the OTP sent to{" "}
                        <span className="text-foreground font-medium">
                          {otpForEmail}
                        </span>
                        . Account type:{" "}
                        <span className="text-foreground font-medium">
                          {staffKind === "admin" ? "Admin" : "Scholar"}
                        </span>
                        .
                      </p>
                      <div className="text-muted-foreground flex flex-wrap gap-x-3 gap-y-1 rounded-lg border bg-muted/40 px-3 py-2 text-xs">
                        <span className="text-foreground inline-flex items-center gap-1 font-medium">
                          <User className="h-3.5 w-3.5" />
                          {signupFirstName.trim() || "—"}{" "}
                          {signupLastName.trim() || ""}
                        </span>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="signup-otp">One-time code</Label>
                        <Input
                          id="signup-otp"
                          inputMode="numeric"
                          autoComplete="one-time-code"
                          value={otpCode}
                          onChange={e =>
                            setOtpCode(e.target.value.replace(/\s/g, ""))
                          }
                          required
                          className="h-11"
                        />
                      </div>
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={backToSignupDetails}
                        >
                          ← Edit details
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={resendIn > 0 || isSubmitting}
                          onClick={handleResendOtp}
                        >
                          {resendIn > 0
                            ? `Resend in ${resendIn}s`
                            : "Resend OTP"}
                        </Button>
                      </div>
                      <Button
                        type="submit"
                        disabled={isSubmitting}
                        className="bg-primary hover:bg-primary/90 h-11 w-full"
                      >
                        {isSubmitting ? (
                          <span className="flex items-center gap-2">
                            <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                            Creating account…
                          </span>
                        ) : (
                          <>
                            <UserPlus className="mr-2 h-4 w-4" />
                            Verify &amp; go to dashboard
                          </>
                        )}
                      </Button>
                    </form>
                  )}
                </TabsContent>
              </Tabs>

              <p className="text-muted-foreground mt-6 text-center text-xs">
                Contributor?{" "}
                <Link
                  to="/login"
                  className="text-primary font-medium underline"
                >
                  User portal login
                </Link>
              </p>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default StaffLogin;
